import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, IconButton, Typography, Card, CardContent, 
  Chip, TextField, InputAdornment, Tooltip, CircularProgress, 
  Alert, Snackbar, Fade, Avatar, ButtonGroup, useMediaQuery, 
  Container, Button, Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import { 
  Delete, Edit, Search, Person, Refresh, PersonOutline, Add,
  NavigateBefore, NavigateNext
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import UpdateUserModal from "./UpdateUserModal";
import ModalCreateAkun from "./ModalCreateAkun";

const styles = {
  userList: {
    "& .MuiTableCell-root": { fontSize: "0.875rem", padding: "16px" },
    "& .MuiTableRow-root:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
  },
  searchBar: {
    marginBottom: 0, display: "flex", gap: 16, alignItems: "center",
    justifyContent: "space-between", flexWrap: "wrap"
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexWrap: "wrap", marginBottom: 2
  },
  cardHeader: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)", paddingBottom: 2,
    paddingTop: 2, backgroundColor: "#f9fafc"
  },
  roleChip: {
    fontWeight: "bold", textTransform: "capitalize", minWidth: "80px"
  },
  emptyState: {
    textAlign: "center", padding: "40px 0", color: "text.secondary"
  },
  avatar: {
    width: 32, height: 32, fontSize: "0.875rem",
    backgroundColor: "#1976d2", marginRight: 1
  },
  contentNoPadding: {
    padding: 0, '&:last-child': { paddingBottom: 0 }
  },
  paginationContainer: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px", borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    flexWrap: "wrap", gap: 2
  },
  controlsContainer: {
    display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap"
  }
};

export const UserList = () => {
  const [users, setUsers] = useState([]);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  
  const isMobile = useMediaQuery('(max-width:800px)');

  const authUser = useSelector((s) => s.authAdmin?.user);
  const userRole = authUser?.role || '';
  const isAdmin = userRole === 'admin';
  const isTFL = userRole === 'TFL';
  const canDelete = isAdmin;
  const canEdit = isAdmin || isTFL;
  
  // Debounce search untuk menghindari request berlebihan
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getUsers();
    }, 500); // Delay 500ms setelah user berhenti mengetik

    return () => clearTimeout(timeoutId);
  }, [currentPage, perPage, searchQuery]);

  const getUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_URL}/user`, {
        params: { 
          page: currentPage, 
          limit: perPage, 
          search: searchQuery 
        },
        withCredentials: true,
      });
      
      setUsers(response.data.data);
      setHasNextPage(response.data.hasNextPage);
      setHasPrevPage(response.data.hasPrevPage);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Gagal mengambil data user");
      setLoading(false);
      Swal.fire("Error!", "Gagal mengambil data user", "error");
    }
  };

  const deleteUser = async (id) => {
    try {
      const confirm = await Swal.fire({
        title: "Apakah Anda yakin?",
        text: "User akan dihapus secara permanen!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Ya, hapus!",
      });

      if (confirm.isConfirmed) {
        await axios.delete(`${process.env.REACT_APP_URL}/deleteuser/${id}`, {
          withCredentials: true,
        });
        setSnackbar({
          open: true, message: "User berhasil dihapus", severity: "success"
        });
        
        // Jika ini adalah data terakhir di halaman, kembali ke halaman sebelumnya
        if (users.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          getUsers();
        }
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true, message: "Terjadi kesalahan saat menghapus user", severity: "error"
      });
    }
  };

  const handleUpdateClick = (user) => {
    setSelectedUser(user);
    setUpdateModalOpen(true);
  };

  const handleUpdateModalClose = () => {
    setUpdateModalOpen(false);
    setSelectedUser(null);
  };

  const handleCreateModalToggle = () => {
    setCreateModalOpen(!createModalOpen);
  };

  const handleUserCreated = () => {
    setCurrentPage(1);
    getUsers();
    setSnackbar({
      open: true, message: "User baru berhasil ditambahkan", severity: "success"
    });
  };

  const handleUserUpdated = () => {
    getUsers();
    setSnackbar({
      open: true, message: "User berhasil diupdate", severity: "success"
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (hasPrevPage) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (hasNextPage) setCurrentPage(currentPage + 1);
  };

  const handleRefresh = () => {
    getUsers();
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const getRoleColor = (role) => {
    switch (role.toLowerCase()) {
      case 'admin': return { bg: '#28a745', color: 'white' };
      case 'surveyor': return { bg: '#007bff', color: 'white' };
      case 'verifikator': return { bg: '#00ff1aff', color: 'white' };
      case 'inputdata': return { bg: '#f7e929ff', color: 'white' };
      case 'koordinator tfl': return { bg: '#00695C', color: 'white' };
      case 'tfl': return { bg: '#00838F', color: 'white' };
      default: return { bg: '#6c757d', color: 'white' };
    }
  };

  const getInitials = (name) => name.charAt(0).toUpperCase();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Container 
        maxWidth={false} 
        sx={{ 
          pt: 0, pb: 0,
          ml: { xs: 0, sm: isMobile ? 0 : "230px" }, 
          pr: { xs: 2, sm: 4 },
          pl: { xs: 2, sm: isMobile ? 2 : 0 },
          width: { xs: "100%", sm: isMobile ? "100%" : "calc(100% - 210px)" },
          mt: 0
        }}
      >
        <Card elevation={2}>
          <CardContent sx={styles.cardHeader}>
            <Box sx={styles.header}>
              <Typography variant="h5" component="h1" fontWeight="500">
                <PersonOutline sx={{ verticalAlign: "middle", mr: 1 }} />
                Daftar Pengguna
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button 
                  variant="contained" color="primary" size="small" 
                  startIcon={<Add />} onClick={handleCreateModalToggle}
                >
                  CREATE USER
                </Button>
                <Tooltip title="Refresh data">
                  <Button onClick={handleRefresh} size="small">
                    <Refresh />
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
          
          <CardContent sx={{ pb: 0, pt: 2 }}>
            <Box sx={styles.searchBar}>
              <TextField
                placeholder="Cari berdasarkan username, email, atau role..."
                variant="outlined" 
                size="small" 
                fullWidth
                value={searchQuery} 
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small" 
                        onClick={handleClearSearch}
                        edge="end"
                      >
                        <Typography variant="body2" color="textSecondary">
                          ✕
                        </Typography>
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </CardContent>
          
          <CardContent sx={styles.contentNoPadding}>
            {error && <Alert severity="error" sx={{ mb: 0 }}>{error}</Alert>}
            
            {loading ? (
              <Box sx={styles.emptyState}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Memuat data pengguna...
                </Typography>
              </Box>
            ) : users.length === 0 ? (
              <Box sx={styles.emptyState}>
                <Person sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {searchQuery ? "Tidak ada pengguna yang sesuai dengan pencarian" : "Belum ada data pengguna"}
                </Typography>
                {searchQuery && (
                  <Button 
                    variant="outlined" 
                    onClick={handleClearSearch}
                    sx={{ mt: 2 }}
                  >
                    Hapus Pencarian
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0} variant="outlined" 
                  sx={{ borderRadius: 0, border: 0 }}>
                  <Table sx={styles.userList}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}>
                        <TableCell>ID</TableCell>
                        <TableCell>Pengguna</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Kecamatan</TableCell>
                        <TableCell>Desa/Kelurahan</TableCell>
                        <TableCell align="center">Aksi</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell width="10%">{user.id}</TableCell>
                          <TableCell width="25%">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar sx={styles.avatar}>{getInitials(user.username)}</Avatar>
                              {user.username}
                            </Box>
                          </TableCell>
                          <TableCell width="35%">{user.email}</TableCell>
                          <TableCell width="15%">
                            <Chip 
                              label={user.role}
                              sx={{
                                ...styles.roleChip,
                                backgroundColor: getRoleColor(user.role).bg,
                                color: getRoleColor(user.role).color
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell width="12%">
                            {user.kecamatan || '-'}
                          </TableCell>
                          <TableCell width="12%">
                            {user.desaKelurahan || '-'}
                          </TableCell>
                          <TableCell align="center" width="15%">
                            <ButtonGroup variant="outlined" size="small">
                              {canEdit && (
                                <Tooltip title="Edit pengguna">
                                  <IconButton 
                                    color="primary" 
                                    onClick={() => handleUpdateClick(user)} 
                                    size="small"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canDelete && (
                                <Tooltip title="Hapus pengguna">
                                  <IconButton 
                                    color="error" 
                                    onClick={() => deleteUser(user.id)} 
                                    size="small"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </ButtonGroup>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <Box sx={styles.paginationContainer}>
                  <Box sx={styles.controlsContainer}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Per Page</InputLabel>
                      <Select 
                        value={perPage} 
                        label="Per Page" 
                        onChange={handlePerPageChange}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Typography variant="body2" color="text.secondary">
                      Halaman {currentPage} • {users.length} data
                      {searchQuery && " • Hasil pencarian"}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined" 
                      size="small"
                      startIcon={<NavigateBefore />}
                      onClick={handlePrevPage}
                      disabled={!hasPrevPage}
                    >
                      {isMobile ? "" : "Prev"}
                    </Button>
                    <Button
                      variant="outlined" 
                      size="small"
                      endIcon={<NavigateNext />}
                      onClick={handleNextPage}
                      disabled={!hasNextPage}
                    >
                      {isMobile ? "" : "Next"}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {updateModalOpen && (
          <UpdateUserModal 
            isOpen={updateModalOpen} 
            toggle={handleUpdateModalClose}  
            user={selectedUser} 
            onUserUpdated={handleUserUpdated} 
          />
        )}
        
        <ModalCreateAkun 
          isOpen={createModalOpen} 
          toggle={handleCreateModalToggle}
          onUserCreated={handleUserCreated}
          userRole={userRole}
        />
        
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={5000} 
          onClose={handleCloseSnackbar} 
          TransitionComponent={Fade}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            variant="filled" 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default UserList;